import {
  Autocomplete,
  Box,
  CardMedia,
  Icon,
  Paper,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import React, { useEffect, useState } from 'react'
import defaultAvatar from 'assets/images/default_avatar.png'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import TimelineIcon from '@mui/icons-material/Timeline'
import AddIcon from '@mui/icons-material/Add'
import {
  getAllEmployeeData,
  getDepartmentData,
} from 'services/api/modules/recruitment/job-application'
import { useQuery } from 'react-query'
import AdxButton from 'components/custom/AdxButton'

const PersonalitySideBar = ({ evaluateTeamData, showScore, handleOpenModal }) => {
  const chartColors = [
    'rgba(245, 210, 22)',
    'rgba(126, 61, 121)',
    'rgba(32, 81, 238)',
    'rgba(255, 92, 0)',
    'rgba(239, 171, 255)',
  ]

  const [type, setType] = useState('Employee focused comparison')
  const handleChange = (event) => {
    setType(event.target.value)
  }

  const handleOpen = () => {
    handleOpenModal()
  }

  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [selectedDepartment, setSelectedDepartment] = useState(null)
  const [benchmark, setBenchmark] = useState(null)
  const [compareArray, setCompareArray] = useState([])

  const combinedArray = [...(benchmark !== null ? [benchmark] : []), ...compareArray].slice(0, 5)

  const hasBenchmark = !!benchmark

  useEffect(() => {
    if (selectedEmployee !== null) {
      setBenchmark(selectedEmployee)
    } else if (selectedDepartment !== null) {
      setBenchmark(selectedDepartment)
    } else {
      setBenchmark(null)
    }
  }, [selectedEmployee, selectedDepartment])

  const clearEmployeeSelection = () => {
    setSelectedEmployee(null)
  }
  const clearDepartmentSelection = () => {
    setSelectedDepartment(null)
  }

  // API call
  const {
    isLoading: allEmployeeIsLoading,
    error: allEmployeeError,
    data: allEmployeeData,
  } = useQuery(
    ['getAllEmployeeData'],
    async () => {
      const { data } = await getAllEmployeeData()
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const {
    isLoading: departmentIsLoading,
    error: departmentError,
    data: departmentData,
  } = useQuery(
    ['getDepartmentData'],
    async () => {
      const { data } = await getDepartmentData()
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const optionsForInput1 = allEmployeeData
    ? allEmployeeData.filter((option) => !compareArray?.includes(option))
    : []
  const optionsForInput2 = allEmployeeData
    ? allEmployeeData.filter((option) => ![benchmark]?.includes(option))
    : []

  const removeOption = (index) => {
    const updatedCompareArray = [...compareArray]
    updatedCompareArray.splice(index, 1)
    setCompareArray(updatedCompareArray)
  }

  useEffect(() => {
    evaluateTeamData({ combinedArray, type, compareArray, benchmark })
  }, [benchmark, compareArray])

  // clear array on type switch
  useEffect(() => {
    setSelectedEmployee(null)
    setSelectedDepartment(null)
    setCompareArray([])
    setBenchmark(null)
  }, [type])

  return (
    <Box
      sx={{
        width: '100%',
        // maxWidth: '400px',
        height: '100%',
        display: showScore ? 'none' : 'flex',
        flexDirection: 'column',
        justifyContent:
          allEmployeeData?.length > 0 || departmentData?.length > 0 ? 'start' : 'space-between',
        gap: '24px',
        padding: '24px 24px 24px 20px',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Select
          value={type}
          IconComponent={ExpandMoreIcon}
          defaultValue="Employee focused comparison"
          onChange={handleChange}
          // nowrap
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
            backgroundColor: 'gray-background.main',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: 'gray-line.main',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'gray-line.main',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'gray-line.main',
            },
          }}
          renderValue={(params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Icon sx={{ fontSize: '21px' }}>query_stats</Icon>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {type}
              </Typography>
            </Box>
          )}
        >
          <AdxMenuItem
            sx={{ margin: 1, borderRadius: '4.5px' }}
            value="Employee focused comparison"
            selectedvalue={type}
          >
            Employee focused comparison
          </AdxMenuItem>
          <AdxMenuItem
            sx={{ margin: 1, borderRadius: '4.5px' }}
            value="Employee department fit"
            selectedvalue={type}
          >
            Employee department fit
          </AdxMenuItem>
          <AdxMenuItem
            sx={{ margin: 1, borderRadius: '4.5px' }}
            value="Department personality comparison"
            selectedvalue={type}
          >
            Department personality comparison
          </AdxMenuItem>
        </Select>
      </Box>
      {allEmployeeData?.length > 0 || departmentData?.length > 0 ? (
        <>
          {type !== 'Department personality comparison' && (
            <Box display="flex" flexDirection="column" gap="8px">
              {allEmployeeIsLoading || departmentIsLoading ? (
                <Skeleton variant="rectangular" height="21px" width="40%" />
              ) : (
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Select benchmark
                </Typography>
              )}

              {allEmployeeIsLoading || departmentIsLoading ? (
                <Skeleton variant="rectangular" height="73px" width="100%" />
              ) : (
                <Box
                  border={1}
                  borderColor="gray-line.main"
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderRadius: '4.5px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingX: '12px',
                    }}
                    width="56px"
                  >
                    <TimelineIcon sx={{ color: 'green-01.main' }} />
                  </Box>
                  <Autocomplete
                    disabled={combinedArray.length === 5}
                    value={
                      type === 'Employee focused comparison' ? selectedEmployee : selectedDepartment
                    }
                    onChange={(e, value) => {
                      if (type === 'Employee focused comparison') {
                        setSelectedEmployee(value)
                      } else {
                        setSelectedDepartment(value)
                      }
                    }}
                    popupIcon={<ExpandMoreIcon />}
                    options={
                      type === 'Employee focused comparison'
                        ? optionsForInput1 || []
                        : departmentData || []
                    }
                    getOptionLabel={(option) => option?.name || ''}
                    sx={{
                      borderLeft: 1,
                      borderColor: 'gray-line.main',
                      '& .MuiInputBase-root': {
                        outline: 'none',
                        bgcolor: 'none',
                        py: 2,
                        marginY: 0,
                        '&.Mui-disabled': {
                          backgroundColor: 'transparent',
                          marginY: 0,
                          py: 2,
                        },
                        '&.Mui-focused': {
                          border: 'none',
                          boxShadow: 'none',
                        },
                        // outlineColor: 'red !important',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.25,
                          py: 0,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 0,
                        },

                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '& .MuiAutocomplete-option': {
                            backgroundColor: 'gray-background.main',
                          },
                          border: 0,
                        },
                      },

                      height: '100%',
                      width: '100%',
                    }}
                    ListboxProps={{
                      sx: {
                        '& .MuiAutocomplete-option[aria-selected=true]': {
                          backgroundColor: 'transparent',
                          '&.Mui-focused': {
                            backgroundColor: 'gray-background.main',
                          },
                        },
                        '& .MuiAutocomplete-option[aria-selected=false]': {
                          backgroundColor: 'transparent',
                          '&.Mui-focused': {
                            backgroundColor: 'gray-background.main',
                          },
                        },
                        '&::-webkit-scrollbar': {
                          width: '6px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          borderRadius: '4.5px',
                          backgroundColor: 'rgba(206,208,212)',
                        },
                      },
                    }}
                    renderInput={(params) => {
                      if (type === 'Employee focused comparison') {
                        return (
                          <>
                            {selectedEmployee ? (
                              <Box display="flex" alignItems="center" gap="16px" padding="16px">
                                <CardMedia
                                  component="img"
                                  sx={{
                                    height: 45,
                                    width: 45,
                                    objectFit: 'cover',
                                    borderRadius: '100%',
                                  }}
                                  src={selectedEmployee?.profile_image_url ?? defaultAvatar}
                                />
                                <Box sx={{ width: '100%' }}>
                                  <Typography
                                    variant="subtitle1"
                                    color="dark-text.main"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      wordBreak: 'break-word',
                                      overflowWrap: 'break-word',
                                    }}
                                  >
                                    {selectedEmployee?.name}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    color="light-gray-text.main"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      wordBreak: 'break-word',
                                      overflowWrap: 'break-word',
                                    }}
                                  >
                                    {selectedEmployee?.email}
                                  </Typography>
                                </Box>
                                <Icon
                                  onClick={clearEmployeeSelection}
                                  sx={{ color: 'error.main', cursor: 'pointer', fontSize: '21px' }}
                                >
                                  clear
                                </Icon>
                              </Box>
                            ) : (
                              <Box>
                                <TextField
                                  {...params}
                                  placeholder={
                                    combinedArray.length === 5
                                      ? 'Please remove one staff'
                                      : 'Search or select to add'
                                  }
                                  InputLabelProps={{
                                    sx: {
                                      display: 'none',
                                    },
                                  }}
                                  variant="outlined"
                                  inputProps={{
                                    ...params.inputProps,
                                  }}
                                />
                              </Box>
                            )}
                          </>
                        )
                      } else {
                        return (
                          <>
                            {selectedDepartment ? (
                              <Box display="flex" alignItems="center" gap="16px" padding="16px">
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    borderRadius: '100%',
                                    border: 1,
                                    borderColor: 'gray-line.main',
                                    padding: 1,
                                    backgroundColor: 'gray-background.main',
                                  }}
                                >
                                  <Icon sx={{ color: 'green-01.main' }}>groups</Icon>
                                </Box>
                                <Box sx={{ width: '100%' }}>
                                  <Typography variant="subtitle1" color="dark-text.main">
                                    {selectedDepartment?.name}
                                  </Typography>

                                  <Typography variant="body2" color="light-gray-text.main">
                                    {selectedDepartment?.staff_count} members
                                  </Typography>
                                </Box>
                                <Icon
                                  onClick={clearDepartmentSelection}
                                  sx={{
                                    color: 'error.main',
                                    cursor: 'pointer',
                                    fontSize: '21px',
                                  }}
                                >
                                  clear
                                </Icon>
                              </Box>
                            ) : (
                              <Box>
                                <TextField
                                  {...params}
                                  placeholder="Search or select to add"
                                  InputLabelProps={{
                                    sx: {
                                      display: 'none',
                                    },
                                  }}
                                  variant="outlined"
                                  inputProps={{
                                    ...params.inputProps,
                                  }}
                                />
                              </Box>
                            )}
                          </>
                        )
                      }
                    }}
                    PaperComponent={({ children }) => <Paper elevation={2}>{children}</Paper>}
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    getOptionDisabled={(option) => option.has_data === false}
                    noOptionsText={
                      type === 'Employee focused comparison'
                        ? 'No employees available'
                        : 'No departments available'
                    }
                    renderOption={(props, option) => {
                      if (type === 'Employee focused comparison') {
                        return (
                          <Box
                            {...props}
                            display="flex"
                            alignItems="center"
                            gap="16px"
                            padding="16px"
                          >
                            <CardMedia
                              component="img"
                              sx={{
                                height: 45,
                                width: 45,
                                objectFit: 'cover',
                                borderRadius: '100%',
                              }}
                              src={option?.profile_image_url ?? defaultAvatar}
                            />
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="subtitle1" color="dark-text.main">
                                {option?.name}
                              </Typography>

                              <Typography
                                variant="body2"
                                color={option.has_data ? 'light-gray-text.main' : 'error.main'}
                              >
                                {option?.email} {option.has_data ? null : `(no data)`}
                                {option.has_data === false && (
                                  <Icon
                                    sx={{
                                      color: 'error.main',
                                      fontSize: 16,
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    error
                                  </Icon>
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        )
                      } else {
                        return (
                          <Box
                            {...props}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Typography padding="12px" variant="subtitle1" color="dark-text.main">
                              {option?.name}
                            </Typography>
                            {option.has_data === false && (
                              <Box
                                sx={{ display: 'flex', justifyContent: 'end', marginLeft: 'auto' }}
                              >
                                <Icon
                                  sx={{
                                    color: 'error.main',
                                    fontSize: 16,
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  error
                                </Icon>
                                <Typography variant="body2" color="error.main">
                                  (no data)
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
          <Box display="flex" flexDirection="column" gap="8px">
            {allEmployeeIsLoading || departmentIsLoading ? (
              <Skeleton variant="rectangular" height="21px" width="40%" />
            ) : (
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {type === 'Department personality comparison'
                  ? 'Select department to compare'
                  : 'Select staff to compare'}
              </Typography>
            )}
            <Box>
              {allEmployeeIsLoading || departmentIsLoading ? (
                <Skeleton variant="rectangular" height="73px" width="100%" />
              ) : (
                <Autocomplete
                  // disableClearable
                  value={compareArray}
                  key={type}
                  multiple
                  filterSelectedOptions
                  options={
                    type === 'Department personality comparison'
                      ? departmentData || []
                      : optionsForInput2 || []
                  }
                  popupIcon={<AddIcon />}
                  getOptionLabel={(option) => option?.name || ''}
                  onChange={(e, value) => {
                    if (benchmark) {
                      setCompareArray(value.slice(0, 4))
                    } else {
                      setCompareArray(value.slice(0, 5))
                    }
                  }}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search or select to add"
                      InputLabelProps={{
                        sx: {
                          display: 'none',
                        },
                      }}
                      variant="outlined"
                      inputProps={{
                        ...params.inputProps,
                      }}
                    />
                  )}
                  sx={{
                    border: 1,
                    borderColor: 'gray-line.main',
                    borderRadius: '4.5px',
                    '& .MuiInputBase-root': {
                      outline: 'none',
                      bgcolor: 'none',
                      marginY: 0,
                      py: '6px',
                      px: '8px',
                      '&.Mui-disabled': {
                        backgroundColor: 'transparent',
                        marginY: 0,
                        py: 1,
                      },
                      '&.Mui-focused': {
                        border: 'none',
                        boxShadow: 'none',
                      },
                      // outlineColor: 'red !important',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4.5px',
                        py: 0,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 0,
                      },

                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '& .MuiAutocomplete-option': {
                          backgroundColor: 'gray-background.main',
                        },
                        border: 0,
                      },
                    },

                    height: '100%',
                    width: '100%',
                  }}
                  ListboxProps={{
                    sx: {
                      '& .MuiAutocomplete-option[aria-selected=true]': {
                        backgroundColor: 'transparent',
                        '&.Mui-focused': {
                          backgroundColor: 'gray-background.main',
                        },
                      },
                      '& .MuiAutocomplete-option[aria-selected=false]': {
                        backgroundColor: 'transparent',
                        '&.Mui-focused': {
                          backgroundColor: 'gray-background.main',
                        },
                      },
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        borderRadius: '4.5px',
                        backgroundColor: 'rgba(206,208,212)',
                      },
                    },
                  }}
                  PaperComponent={({ children }) => <Paper elevation={2}>{children}</Paper>}
                  isOptionEqualToValue={(option, value) => option.name === value.name}
                  getOptionDisabled={(option) => option.has_data === false}
                  renderOption={(props, option) => {
                    if (type !== 'Department personality comparison') {
                      return (
                        <Box
                          {...props}
                          display="flex"
                          alignItems="center"
                          gap="16px"
                          padding="16px"
                        >
                          <CardMedia
                            component="img"
                            sx={{
                              height: 45,
                              width: 45,
                              objectFit: 'cover',
                              borderRadius: '100%',
                            }}
                            src={option?.profile_image_url ?? defaultAvatar}
                          />
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="subtitle1" color="dark-text.main">
                              {option?.name}
                            </Typography>

                            <Typography
                              variant="body2"
                              color={option.has_data ? 'light-gray-text.main' : 'error.main'}
                            >
                              {option?.email} {option.has_data ? null : `(no data)`}
                              {option.has_data === false && (
                                <Icon
                                  sx={{
                                    color: 'error.main',
                                    fontSize: 16,
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  error
                                </Icon>
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    } else {
                      return (
                        <Box
                          {...props}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Typography padding="12px" variant="subtitle1" color="dark-text.main">
                            {option?.name}
                          </Typography>
                          {option.has_data === false && (
                            <Box
                              sx={{ display: 'flex', justifyContent: 'end', marginLeft: 'auto' }}
                            >
                              <Icon
                                sx={{
                                  color: 'error.main',
                                  fontSize: 16,
                                  verticalAlign: 'middle',
                                }}
                              >
                                error
                              </Icon>
                              <Typography variant="body2" color="error.main">
                                (no data)
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )
                    }
                  }}
                />
              )}
            </Box>
            <Box display="flex" flexDirection="column">
              {compareArray.map((option, key) => (
                <Box
                  key={key}
                  border={1}
                  borderColor="gray-line.main"
                  borderTop={key === 0 ? 1 : 0}
                  borderTopColor="gray-line.main"
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderTopLeftRadius: key === 0 ? '4.5px' : null,
                    borderTopRightRadius: key === 0 ? '4.5px' : null,
                    borderBottomLeftRadius: key === compareArray.length - 1 ? '4.5px' : null,
                    borderBottomRightRadius: key === compareArray.length - 1 ? '4.5px' : null,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingX: '12px',
                    }}
                    width="56px"
                  >
                    <ShowChartIcon
                      sx={{
                        color: chartColors[key % chartColors.length],
                      }}
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="16px"
                    padding="16px"
                    borderLeft={1}
                    borderColor="gray-line.main"
                    width="100%"
                  >
                    {type === 'Department personality comparison' ? (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: '100%',
                            border: 1,
                            borderColor: 'gray-line.main',
                            backgroundColor: 'gray-background.main',
                          }}
                        >
                          <Icon sx={{ color: 'green-01.main' }}>groups</Icon>
                        </Box>
                      </>
                    ) : (
                      <CardMedia
                        component="img"
                        sx={{
                          height: 45,
                          width: 45,
                          objectFit: 'cover',
                          borderRadius: '100%',
                        }}
                        src={option?.profile_image_url ?? defaultAvatar}
                      />
                    )}
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="subtitle1"
                        color="dark-text.main"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {option?.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="light-gray-text.main"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {type === 'Department personality comparison'
                          ? `${option?.staff_count} ${
                              option?.staff_count === 1 ? 'member' : 'members'
                            }`
                          : option?.email}
                      </Typography>
                    </Box>
                    <Icon
                      onClick={() => removeOption(key)}
                      sx={{
                        color: 'error.main',
                        cursor: 'pointer',
                        fontSize: '21px',
                      }}
                    >
                      clear
                    </Icon>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      ) : (
        <EmptyState handleOpen={handleOpen} />
      )}
    </Box>
  )
}

export default PersonalitySideBar

export const EmptyState = ({ handleOpen }) => {
  const handleOpenModal = () => {
    handleOpen()
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <Box />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <Box
          bgcolor="gray-background.main"
          sx={{
            borderRadius: '100%',
            width: '90px',
            height: '90px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Icon sx={{ fontSize: '36px', color: 'light-gray-text.main' }}>query_stats</Icon>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Typography variant="subtitle1">There is no data available to compare</Typography>
          <Typography variant="body1" color="light-gray-text.main" textAlign="center">
            Invite your staff to take the work personality test and start your comparison analysis
          </Typography>
        </Box>
      </Box>
      <AdxButton
        variant={'contained'}
        onClick={handleOpenModal}
        sx={{ width: '100%', boxShadow: 'none' }}
      >
        Invite staff
      </AdxButton>
    </Box>
  )
}
